import type { ReactNode } from 'react';
import { Alert, Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-react';

interface ConfirmDialogProps {
  title: string;
  message: ReactNode;
  /** Primary action label. Default "Confirm". */
  confirmLabel?: string;
  /** Cancel action label. Default "Cancel". */
  cancelLabel?: string;
  /** Disables dismiss + buttons + shows loading on the primary action. */
  busy?: boolean;
  /** Wraps message in a warning Alert to signal destructive actions. */
  danger?: boolean;
  /** Failure of the confirmed action, shown INSIDE the dialog. Required because the
   *  dialog stays open on failure and the host page's own Alert sits behind the modal
   *  overlay (rgba(242,243,243,0.9)) — i.e. unreadable. */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  busy = false,
  danger = false,
  error = null,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      visible
      onDismiss={() => {
        if (!busy) onCancel();
      }}
      header={title}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancel} disabled={busy}>
              {cancelLabel}
            </Button>
            <Button variant="primary" onClick={onConfirm} loading={busy}>
              {confirmLabel}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        {danger ? <Alert type="warning">{message}</Alert> : message}
        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
